import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import type { ObjectStoreExclusionRule } from '@repo/api-gw-sdk';
import { useEffect, useState } from 'react';

import { PanelWrapper } from '@/components/panels/panelWrapper';
import { dayjs } from '@/utils/dayjs';

export interface ObjectStoreBackupConfigurationPanelProps {
  entity: ObjectStoreExclusionRule;
  allRules: ObjectStoreExclusionRule[];
  onSave: (rule: ObjectStoreExclusionRule) => void;
  onClose: () => void;
}

export function ObjectStoreBackupConfigurationPanel(
  props: ObjectStoreBackupConfigurationPanelProps
) {
  const [data, setData] = useState(props.entity);

  const existingRule = props.allRules.find(
    (x) => x.id !== data.id && x.bucket === data.bucket
  );

  useEffect(() => {
    if (existingRule) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setData((state) => ({
        ...state,
        include: existingRule.include,
      }));
    }
  }, [existingRule]);

  return (
    <PanelWrapper
      header={{
        onClose: props.onClose,
        title: props.entity.id ? 'Edit Rule' : 'Create Rule',
      }}
    >
      <Box className='h-full flex flex-col p-[20px]'>
        <Typography variant='body1'>
          Set up a rule to either include or exclude specific S3 objects from
          being backed up.
        </Typography>
        <Stack direction='column' gap='20px'>
          <FormControl className='w-1/2 mt-[24px]'>
            <FormLabel>Bucket Name (Set * for all buckets)</FormLabel>
            <TextField
              defaultValue={data.bucket}
              inputProps={{ 'data-testid': 'bucket-name' }}
              onChange={(event) => {
                setData({
                  ...data,
                  bucket: event.target.value,
                });
              }}
            />
          </FormControl>
          <FormControl className='w-1/2'>
            <FormLabel>Rule Type</FormLabel>
            <FormControlLabel
              disabled={!!existingRule}
              checked={!!data.include}
              onChange={() => {
                setData({
                  ...data,
                  include: true,
                });
              }}
              control={<Radio className='pr-[12px]' />}
              label='Include'
            />
            <FormControlLabel
              disabled={!!existingRule}
              checked={!data.include}
              onChange={() => {
                setData({
                  ...data,
                  include: false,
                });
              }}
              control={<Radio className='pr-[12px]' />}
              label='Exclude'
            />
          </FormControl>
          <Divider sx={{ margin: '12px -24px' }} />
          <FormControl className='w-1/2'>
            <FormLabel>{`${data.include ? 'Include' : 'Exclude'} Objects with Prefix:`}</FormLabel>
            <TextField
              value={data.bucketPrefix}
              inputProps={{ 'data-testid': 'bucket-name' }}
              placeholder='path/in/bucket'
              onChange={(event) => {
                setData({
                  ...data,
                  bucketPrefix: event.target.value.replace(/^\/+/, ''),
                });
              }}
            />
          </FormControl>
          <FormControl className='w-1/2'>
            <FormLabel>{`${data.include ? 'Include Objects Modified After:' : 'Exclude Objects Modified Before:'} `}</FormLabel>
            <DatePicker
              value={
                data.modificationDate ? dayjs.utc(data.modificationDate) : null
              }
              onChange={(newValue) => {
                setData({
                  ...data,
                  modificationDate: newValue?.toDate() || null,
                });
              }}
            />
          </FormControl>
        </Stack>
      </Box>
      <Box className='flex justify-end px-[20px] py-[16px]'>
        <Button
          variant='outlined'
          className='mr-[12px]'
          onClick={props.onClose}
        >
          Cancel
        </Button>
        <Button
          variant='contained'
          data-testid='save-vault'
          disabled={
            !data.bucket || (!data.bucketPrefix && !data.modificationDate)
          }
          onClick={() => props.onSave(data)}
        >
          {props.entity.id ? 'Save Changes' : 'Create Rule'}
        </Button>
      </Box>
    </PanelWrapper>
  );
}
